import {
  getIntersectionRoot,
  getScrollEventTarget,
  getScrollTop,
  scrollContainerMediaQuery,
  scrollTo,
} from '@theme/scroll-container';

const MOTION_ROOT_MARGIN = '-25% 0px -25% 0px';

class SybilGlobalActions extends HTMLElement {
  #lifecycleController;
  #scrollController;
  #motionObserver;
  #backToTop;
  #mobileCta;
  #scrollFrame = 0;

  connectedCallback() {
    this.#lifecycleController?.abort();
    this.#lifecycleController = new AbortController();
    this.#backToTop = this.querySelector('[data-sybil-back-to-top]');
    this.#mobileCta = this.querySelector('[data-sybil-mobile-cta]');

    this.#backToTop?.addEventListener('click', this.#handleBackToTop, {
      signal: this.#lifecycleController.signal,
    });
    scrollContainerMediaQuery.addEventListener('change', this.#bindScrollRoot, {
      signal: this.#lifecycleController.signal,
    });
    document.addEventListener('shopify:section:load', this.#handleSectionLoad, {
      signal: this.#lifecycleController.signal,
    });

    this.#bindScrollRoot();
  }

  disconnectedCallback() {
    this.#motionObserver?.disconnect();
    this.#scrollController?.abort();
    this.#lifecycleController?.abort();
    if (this.#scrollFrame) {
      cancelAnimationFrame(this.#scrollFrame);
      this.#scrollFrame = 0;
    }
  }

  #bindScrollRoot = () => {
    this.#scrollController?.abort();
    this.#scrollController = new AbortController();
    getScrollEventTarget().addEventListener('scroll', this.#queueScrollActionsUpdate, {
      passive: true,
      signal: this.#scrollController.signal,
    });
    this.#updateScrollActions();
    this.#bindMotionObserver();
  };

  #bindMotionObserver = () => {
    this.#motionObserver?.disconnect();
    this.#motionObserver = new IntersectionObserver(this.#handleMotionIntersection, {
      root: getIntersectionRoot(),
      rootMargin: MOTION_ROOT_MARGIN,
    });

    document
      .querySelectorAll('.sybil-motion')
      .forEach((section) => this.#motionObserver.observe(section));
  };

  #handleMotionIntersection = (entries) => {
    entries.forEach((entry) => {
      entry.target.dataset.sybilMotionActive = String(entry.isIntersecting);
    });
  };

  #handleSectionLoad = (event) => {
    const section =
      event.target.closest?.('.sybil-motion') ?? event.target.querySelector?.('.sybil-motion');
    if (section) this.#motionObserver?.observe(section);
  };

  #queueScrollActionsUpdate = () => {
    if (this.#scrollFrame) return;

    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      this.#updateScrollActions();
    });
  };

  #updateScrollActions = () => {
    const hidden = getScrollTop() <= 400;
    if (this.#backToTop && this.#backToTop.hidden !== hidden) this.#backToTop.hidden = hidden;
    if (this.#mobileCta && this.#mobileCta.hidden !== hidden) this.#mobileCta.hidden = hidden;
  };

  #handleBackToTop = () => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scrollTo({ top: 0, behavior: reduceMotion ? 'instant' : 'smooth' });

    const main = document.getElementById('MainContent');
    if (!(main instanceof HTMLElement)) return;
    if (!main.hasAttribute('tabindex')) {
      main.setAttribute('tabindex', '-1');
      main.addEventListener('blur', () => main.removeAttribute('tabindex'), { once: true });
    }
    main.focus({ preventScroll: true });
  };
}

if (!customElements.get('sybil-global-actions')) {
  customElements.define('sybil-global-actions', SybilGlobalActions);
}
